import React, { useState, useEffect, useCallback, useRef } from 'react';
import { websocketVirtualAgent } from 'components/virtualagentws/websocketVirtualAgent';
import { useHVAConversation } from './HVAProductAndOrdersContext';
import { FeedbackMessage, CATALOG_DATA } from './HVAProductAndOrdersData';
import { Col, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getVirtualAgentWsUrl,
  getAuthenticatedWsUrl
} from 'services/virtualagent.service';
import {
  faMicrophone,
  faMicrophoneSlash,
  faPhoneSlash,
  faPhoneVolume
} from '@fortawesome/free-solid-svg-icons';

interface ConversationMessage {
  source: 'user' | 'ai';
  message: string;
}

type HVAConversationWidgetProps = {
  agentId: string;
  agentName: string;
  onError?: (error: Error) => void;
  onFeedback: (message: Omit<FeedbackMessage, 'id' | 'timestamp'>) => void;
  firstMessage: string;
  prompt: string;
  voice: string;
  language: string;
};

const HVADynamicConversationWidget = ({
  agentId,
  agentName,
  onError,
  onFeedback,
  firstMessage,
  prompt,
  voice,
  language
}: HVAConversationWidgetProps): JSX.Element => {
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [clientToolsConfig, setClientToolsConfig] = useState({});

  // Obtenemos el contexto de la conversación
  const {
    clientInfo,
    setIsCustomerIdValidated,
    setIsEmailValidated,
    setRequestTransfer
  } = useHVAConversation();

  // Referencia para el contenedor de mensajes
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Función para hacer scroll hasta el último mensaje
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  };

  // Función de normalización (añadir al inicio del componente)
  const normalizeString = (str: string): string => {
    return str.replace(/[-.\s@]/g, '').toLowerCase();
  };

  // Actualizamos clientTools cuando cambian las validaciones locales
  useEffect(() => {
    let localCustomerIdValidated = false;
    let localEmailValidated = false;

    setClientToolsConfig({
      requestAction: async (parameters: { requestedAction: string }) => {
        const { requestedAction } = parameters;
        switch (requestedAction) {
          case 'TRANSFERTOSUPERVISOR': {
            setRequestTransfer(true);
            onFeedback({
              type: 'transfer',
              message: 'Transfer to human supervisor requested',
              success: true
            });
            return JSON.stringify({
              success: true,
              message: 'Say goodbye to the client.'
            });
          }
          case 'ESCALATE': {
            setRequestTransfer(true);
            onFeedback({
              type: 'transfer',
              message: 'Transfer to disputes departament requested',
              success: true
            });
            return JSON.stringify({
              success: true,
              message: 'Say goodbye to the client.'
            });
          }
          default: {
            onFeedback({
              type: 'info',
              message: `Action requested not available: ${requestedAction}`,
              details: 'Action requested not available',
              success: false
            });
            return JSON.stringify({
              success: false,
              data: null,
              message: `Action requested not available: '${requestedAction}'`
            });
          }
        }
      },

      requestInfo: async (parameters: { requestedInfo: string }) => {
        const { requestedInfo } = parameters;
        switch (requestedInfo) {
          case 'DATETIME': {
            // Retorna fecha y hora actuales
            const now = new Date();
            const options: Intl.DateTimeFormatOptions = {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            };
            const dateTimeString = now.toLocaleString('en-GB', options);

            onFeedback({
              type: 'info',
              message: 'Solicitud de fecha y hora',
              details: `Fecha y hora actual: ${dateTimeString}`,
              success: true
            });

            return JSON.stringify({
              success: true,
              data: dateTimeString,
              message: ''
            });
          }

          case 'PRODUCTS': {
            onFeedback({
              type: 'info',
              message: 'Product information requested',
              success: true
            });

            return JSON.stringify({
              success: true,
              data: {
                productos: CATALOG_DATA.productos.map(prod => ({
                  id: prod.id,
                  nombre: prod.nombre,
                  categoria: prod.categoria,
                  descripcion: prod.descripcion,
                  precio: prod.precio,
                  stock: prod.stock
                }))
              },
              message: 'Products information retrieved'
            });
          }

          case 'ORDERSTATUS': {
            if (!localCustomerIdValidated || !localEmailValidated) {
              onFeedback({
                type: 'info',
                message: 'Order information requested',
                // eslint-disable-next-line
                details: 'Customer ID and email validation required before accessing orders',
                success: false
              });
              return JSON.stringify({
                success: false,
                data: null,
                message: 'Please validate customer ID and email first'
              });
            }

            const customerOrders = CATALOG_DATA.pedidos.filter(
              // eslint-disable-next-line
              order => normalizeString(order.cliente.id) === normalizeString(clientInfo?.customerId || '')
            );

            onFeedback({
              type: 'info',
              message: 'Order information requested',
              success: true
            });

            return JSON.stringify({
              success: true,
              data: {
                pedidos: customerOrders.map(order => ({
                  id: order.id,
                  fecha: order.fecha_pedido,
                  estado: order.estado,
                  total: order.total,
                  productos: order.productos,
                  envio: {
                    estado: order.envio.estado_envio,
                    tracking: order.envio.numero_seguimiento
                  }
                }))
              },
              message: 'Orders information retrieved'
            });
          }

          default: {
            onFeedback({
              type: 'info',
              message: `Requested information not available: ${requestedInfo}`,
              details: 'Requested information not available',
              success: false
            });
            return JSON.stringify({
              success: false,
              data: null,
              message: `Requested information not available: '${requestedInfo}'`
            });
          }
        }
      },

      requestValidation: async (parameters: {
        requestedValidation: string;
        valueProvided: string;
      }) => {
        const { requestedValidation, valueProvided } = parameters;

        switch (requestedValidation) {
          case 'CUSTOMERID': {
            // eslint-disable-next-line
            const isValid = normalizeString(clientInfo?.customerId || '') === normalizeString(valueProvided || '');
            setIsCustomerIdValidated(isValid);
            localCustomerIdValidated = isValid;

            onFeedback({
              type: 'validation',
              message: 'Customer ID validation requested',
              // eslint-disable-next-line
              details: `Customer ID provided: ${valueProvided} Result: ${isValid ? '✓ ID matches' : '✗ ID does not match'}`,
              success: isValid
            });

            return JSON.stringify({
              success: isValid,
              // eslint-disable-next-line
              message: isValid ? 'Customer ID matches' : 'Customer ID does not match',
              validatedId: isValid ? valueProvided : null
            });
          }

          case 'EMAIL': {
            // eslint-disable-next-line
            const isValid = normalizeString(clientInfo?.email || '') === normalizeString(valueProvided || '');
            setIsEmailValidated(isValid);
            localEmailValidated = isValid;

            onFeedback({
              type: 'validation',
              message: 'Email validation requested',
              // eslint-disable-next-line
              details: `Email provided: ${valueProvided} Result: ${isValid ? '✓ Email matches' : '✗ Email does not match'}`,
              success: isValid
            });

            return JSON.stringify({
              success: isValid,
              message: isValid ? 'Email matches' : 'Email does not match',
              validatedEmail: isValid ? valueProvided : null
            });
          }

          default: {
            onFeedback({
              type: 'validation',
              message: `Validation requested not available: ${requestedValidation}`,
              details: 'Validation requested not available',
              success: false
            });
            return JSON.stringify({
              success: false,
              data: null,
              message: `Validation requested not available: '${requestedValidation}'`
            });
          }
        }
      },

      END_CALL: async (parameters: { reason: string }) => {
        // Añadimos mensaje de feedback
        onFeedback({
          type: 'close',
          message: 'Finalización de llamada por el agente',
          details: `Motivo: ${parameters.reason}`,
          success: true
        });

        return JSON.stringify({
          success: true,
          message: `Finalización de llamada`
        });
      }
    });
    // eslint-disable-next-line
  }, [clientInfo, onFeedback, setIsCustomerIdValidated, setIsEmailValidated, setRequestTransfer]);

  // Para adaptarnos a las instrucciones del agente
  const processFirstMessage = (
    message: string,
    agentName: string,
    customerName: string
  ): string => {
    const getSalutation = () => {
      const hour = new Date().getHours();
      if (hour < 13) return 'Buenos días';
      if (hour < 20) return 'Buenas tardes';
      return 'Buenas noches';
    };

    return message
      .replace('{{salutationDayTime}}', getSalutation())
      .replace('{{agentName}}', agentName)
      .replace('{{customerName}}', customerName);
  };

  const conversation = websocketVirtualAgent({
    onConnect: () => {
      console.log('Connected to agent');
      setIsInitialized(true);
      setIsLoading(false);
    },
    onDisconnect: () => {
      console.log('Disconnected from agent');
      onFeedback({
        type: 'close',
        message: 'Hangup executed',
        success: true
      });
      setIsInitialized(false);
      setIsLoading(false);
      setIsCustomerIdValidated(false);
      setIsEmailValidated(false);
    },
    // eslint-disable-next-line
    onError: (message: string, context?: any) => {
      console.error('Conversation error:', message, context);
      setIsLoading(false);
      if (onError) {
        onError(new Error(message));
      }
    },
    onMessage: message => {
      setMessages(prev => [
        ...prev,
        {
          source: message.source,
          message: message.message
        }
      ]);
      setTimeout(scrollToBottom, 0);
    },
    // Añadimos la configuración de client tools
    clientTools: clientToolsConfig
  });

  const requestMicPermission = async (): Promise<boolean> => {
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      setHasPermission(true);
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      setHasPermission(false);
      if (onError) {
        onError(new Error('Microphone permission denied'));
      }
      return false;
    }
  };

  const startConversation = useCallback(async (): Promise<void> => {
    if (!hasPermission) {
      const permitted = await requestMicPermission();
      if (!permitted) return;
    }

    try {
      setIsLoading(true);
      setIsCustomerIdValidated(false);
      setIsEmailValidated(false);
      await conversation.endSession();

      // Obtener URL backend
      let signedUrl;
      if (import.meta.env.VITE_NODE_ENV === 'production') {
        // En producción, usar la función asíncrona
        signedUrl = await getAuthenticatedWsUrl();
      } else {
        // En desarrollo, usar la URL directa
        signedUrl = getVirtualAgentWsUrl();
      }

      // Iniciar sesión con URL firmada
      setMessages([]);
      await conversation.startSession({
        signedUrl,
        overrides: {
          agent: {
            prompt: {
              prompt: prompt
            },
            firstMessage: processFirstMessage(
              firstMessage,
              agentName,
              clientInfo?.name || ''
            ),
            language: language,
            tools: [
              {
                id: 'requestAction',
                name: 'requestAction',
                description: 'Request a specific action to be performed',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedAction: {
                      type: 'string',
                      description:
                        'The action to request (TRANSFERTOSUPERVISOR, ESCALATE)'
                    }
                  },
                  required: ['requestedAction']
                }
              },
              {
                id: 'requestInfo',
                name: 'requestInfo',
                description:
                  'Request specific information about the customer or situation',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedInfo: {
                      type: 'string',
                      description:
                        'The type of information to request (DATETIME, PRODUCTS, ORDERSTATUS)'
                    }
                  },
                  required: ['requestedInfo']
                }
              },
              {
                id: 'requestValidation',
                name: 'requestValidation',
                description: 'Validate customer provided information',
                parameters: {
                  type: 'object',
                  properties: {
                    requestedValidation: {
                      type: 'string',
                      description:
                        'The type of validation to perform (CUSTOMERID, EMAIL)'
                    },
                    valueProvided: {
                      type: 'string',
                      description: 'The value provided by the customer'
                    }
                  },
                  required: ['requestedValidation', 'valueProvided']
                }
              },
              {
                id: 'END_CALL',
                name: 'END_CALL',
                description: 'Ends current conversation',
                parameters: {
                  type: 'object',
                  properties: {
                    reason: {
                      type: 'string',
                      description: 'The reason for ending the call'
                    }
                  },
                  required: []
                }
              }
            ]
          },
          tts: {
            voiceId: voice
          }
        }
      });
    } catch (error) {
      console.error('Failed to start conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [agentId, conversation, hasPermission, onError]);

  const endConversation = useCallback(async (): Promise<void> => {
    try {
      await conversation.endSession();
    } catch (error) {
      console.error('Failed to end conversation:', error);
      if (error instanceof Error && onError) {
        onError(error);
      }
    }
  }, [conversation, onError]);

  // Efecto para inicializar el scroll cuando se montan los mensajes
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Efecto para acceder al micrófono
  useEffect(() => {
    void requestMicPermission();
    return () => {
      if (isInitialized) {
        void endConversation();
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center p-0">
      <Row className="mb-2 mt-2">
        <Col className="pe-0" style={{ maxWidth: '170px', width: '170px' }}>
          <div className="d-flex bg-primary rounded-5 w-100 justify-content-center">
            {!isInitialized ? (
              <Button
                onClick={() => void startConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
                disabled={!hasPermission || isLoading}
              >
                <FontAwesomeIcon icon={faPhoneVolume} className="me-2 fs-8" />
                {isLoading ? 'Connecting...' : 'Start call'}
              </Button>
            ) : (
              <Button
                onClick={() => void endConversation()}
                className="p-2 text-decoration-none text-white"
                variant="link"
              >
                <FontAwesomeIcon icon={faPhoneSlash} className="me-2 fs-8" />
                End call
              </Button>
            )}
          </div>
        </Col>
        <Col className="d-flex justify-content-end">
          <div className="d-flex justify-content-end text-end fs-10 me-2">
            <Row className="text-body-secondary">
              <div className="pt-1">
                Connected:
                {conversation.status === 'connected' ? (
                  <FontAwesomeIcon
                    icon={faPhoneVolume}
                    className="mx-2 me-0 fs-10 text-success"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faPhoneSlash}
                    className="mx-2 me-0 fs-10 text-danger"
                  />
                )}
              </div>
              <div>
                {conversation.status === 'connected' ? (
                  <>
                    Status:
                    {conversation.isSpeaking ? (
                      <FontAwesomeIcon
                        icon={faMicrophone}
                        className="mx-2 me-1 fs-10 text-success"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faMicrophoneSlash}
                        className="mx-1 me-0 fs-10 text-danger"
                      />
                    )}
                  </>
                ) : null}
              </div>
            </Row>
          </div>
        </Col>
      </Row>

      <div
        ref={messagesContainerRef}
        className="p-3 pt-2 pb-2 border rounded scrollbar bg-body-emphasis fs-9 lh-sm shadow-sm"
        style={{
          height: '188px',
          overflowY: 'auto',
          scrollBehavior: 'smooth'
        }}
      >
        {messages.length > 0 && (
          <div className="w-full space-y-2">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`p-2 rounded mb-2 ${
                  msg.source === 'user' ? 'bg-body' : 'bg-body-emphasis'
                }`}
              >
                <strong>{msg.source === 'user' ? 'Customer' : 'Agent'}:</strong>
                &nbsp;
                {msg.message}
              </div>
            ))}
          </div>
        )}
      </div>
      {!hasPermission && (
        <div className="fs-9 p-2 bg-body rounded-lg mt-2">
          Microphone permission is required for conversation
        </div>
      )}
    </div>
  );
};

export default HVADynamicConversationWidget;
